import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'

/*
|--------------------------------------------------------------------------
| Sentry
|--------------------------------------------------------------------------
|
| Centralise toute la configuration Sentry (init, contexte utilisateur,
| capture manuelle). Sans VUE_APP_SENTRY_DSN, chaque helper est un no-op :
| l'application tourne exactement comme avant.
|
*/

const dsn = process.env.VUE_APP_SENTRY_DSN

/**
 * Le SDK n'est armé que si un DSN est fourni.
 */
export const sentryEnabled = Boolean(dsn)

const environment =
    process.env.VUE_APP_SENTRY_ENVIRONMENT ||
    process.env.NODE_ENV ||
    'development'

const isProduction = environment === 'production'

const sampleRate = (value, fallback) => {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 1) : fallback
}

/**
 * Hôtes autorisés à recevoir les en-têtes de tracing distribué.
 */
const tracingOrigins = () => {
    const origins = ['localhost', /^\//]

    try {
        if (process.env.VUE_APP_BACKEND_URL) {
            origins.push(new URL(process.env.VUE_APP_BACKEND_URL).hostname)
        }
    } catch (e) {
        // URL invalide dans le .env : on garde les origines par défaut
    }

    return origins
}

/**
 * Bruit navigateur / extensions qui ne correspond à aucun bug applicatif.
 */
const ignoreErrors = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
    'NavigationDuplicated',
    'Avoided redundant navigation',
    'Request aborted',
    'Network Error',
    'top.GLOBALS',
    /^Loading (CSS )?chunk .* failed/
]

const denyUrls = [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i
]

/**
 * Retire les secrets (jeton, mot de passe) avant l'envoi de l'événement.
 */
const scrub = (event) => {
    if (event.request && event.request.headers) {
        delete event.request.headers['Authorization']
        delete event.request.headers['authorization']
    }

    const sensitive = ['token', 'password', 'password_confirmation', 'secret']

    if (event.extra) {
        sensitive.forEach((key) => {
            if (key in event.extra) event.extra[key] = '[Filtered]'
        })
    }

    return event
}

/**
 * Démarre Sentry. À appeler une seule fois, avant le montage de l'app.
 *
 * @param {import('vue').VueConstructor} Vue
 * @param {import('vue-router').default} router
 */
export function initSentry(Vue, router) {
    if (!sentryEnabled) return

    Sentry.init({
        Vue,
        dsn,
        environment,
        release: process.env.VUE_APP_VERSION
            ? `reborn@${process.env.VUE_APP_VERSION}`
            : undefined,
        // On garde la trace console en dev, Sentry seul suffit en prod
        logErrors: !isProduction,
        attachProps: true,
        attachStacktrace: true,
        tracesSampleRate: sampleRate(
            process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE,
            isProduction ? 0.2 : 1.0
        ),
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                tracingOrigins: tracingOrigins()
            })
        ],
        ignoreErrors,
        denyUrls,
        beforeSend: (event) => scrub(event)
    })
}

/**
 * Associe les événements suivants à l'utilisateur connecté.
 */
export function setSentryUser(user, company) {
    if (!sentryEnabled) return

    if (!user) {
        clearSentryUser()
        return
    }

    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name || user.full_name || undefined
    })

    if (company) {
        Sentry.setTag('company.id', company.id)
        Sentry.setTag('company.name', company.name)
    }
}

/**
 * Purge le contexte utilisateur (déconnexion, session expirée).
 */
export function clearSentryUser() {
    if (!sentryEnabled) return

    Sentry.setUser(null)
    Sentry.setTag('company.id', undefined)
    Sentry.setTag('company.name', undefined)
}

/**
 * Signale une exception en y attachant des données de contexte.
 *
 * @param {Error|*} error
 * @param {{ tags?: Object, extra?: Object }} context
 */
export function captureError(error, context = {}) {
    if (!sentryEnabled) return

    Sentry.withScope((scope) => {
        Object.entries(context.tags || {}).forEach(([key, value]) =>
            scope.setTag(key, value)
        )
        Object.entries(context.extra || {}).forEach(([key, value]) =>
            scope.setExtra(key, value)
        )
        Sentry.captureException(error)
    })
}

/**
 * Trace une action utilisateur, utile pour reconstituer le parcours
 * précédant une erreur.
 */
export function addSentryBreadcrumb(category, message, data = {}) {
    if (!sentryEnabled) return

    Sentry.addBreadcrumb({ category, message, data, level: 'info' })
}

export default Sentry
